import { useEffect, useState } from "react";
import { Star, StarHalf } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// FollowButton — one-tap follow for a species / location / category /
// observation. Requires sign-in; nudges via a toast otherwise.

export default function FollowButton({ type, target, label, variant = "chip", size = "sm", className = "", testid = "follow-btn" }) {
  const { user, requireSignIn } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !target) return;
    http.get("/nature/follows").then((r) => {
      const has = (r.data?.follows || []).some((f) => f.type === type && f.target === target);
      setFollowing(has);
    }).catch(() => {});
  }, [user, type, target]);

  if (!type || !target) return null;

  const toggle = async () => {
    if (!requireSignIn("follow this place")) return;
    setBusy(true);
    try {
      if (following) {
        await http.delete(`/nature/follows?type=${encodeURIComponent(type)}&target=${encodeURIComponent(target)}`);
        setFollowing(false); toast.success("Unfollowed");
      } else {
        await http.post("/nature/follows", { type, target, label: label || target });
        setFollowing(true); toast.success(`Following ${label || target}`);
      }
    } catch { toast.error("Couldn't update follow"); }
    finally { setBusy(false); }
  };

  const base = "inline-flex items-center gap-1.5 font-mono-tech tracking-[0.28em] uppercase transition-colors rounded-full";
  const light = "border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white";
  const active = "bg-[#1B4332] text-[#F5EBDC] border border-[#1B4332]";
  const sz = size === "md" ? "px-4 py-2 text-[10px]" : "px-3 py-1.5 text-[9px]";
  return (
    <button
      onClick={toggle} disabled={busy}
      data-testid={testid}
      className={`${base} ${sz} ${following ? active : light} ${className}`}
    >
      <Star size={size === "md" ? 14 : 12} weight={following ? "fill" : "regular"} />
      {following ? "Following" : `Follow ${type}`}
    </button>
  );
}
